import {
  Drawer,
  useTheme,
  useMediaQuery,
  Grid,
  Typography,
  TextField,
  Autocomplete,
  Button,
} from "@mui/material";
import { DEFAULT_COLOR, YELLOW_COLOR } from "@/constants/color";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { landSizes, downPayments, monthlyPayments } from "@/constants/forms";
import createLead from "@/services/leads/create";
import { useEffect } from "react";
import { Lead } from "@/types/lead";
import updateOne from "@/services/leads/update";
import dayjs from "dayjs";
import { useUsers } from "@/hooks/useUsers";
import { get } from "lodash";
import { useProjects } from "@/services/useProjects";
import { useTypes } from "@/services/useTypes";
import { enqueueSnackbar } from "notistack";

interface LeadDrawerProps {
  open: boolean;
  onClose: () => void;
  lead?: Lead | null;
}

type Projects = "Hacienda Vista Juriquilla" | "Novotech SSEISO";

const leadSchema = yup.object({
  name: yup.string().required("Nombre es requerido"),
  user: yup.string().optional(),
  phone: yup
    .string()
    .matches(/^[0-9]{10}$/, "Teléfono inválido")
    .required("Teléfono es requerido"),
  email: yup.string().email("Correo inválido").optional(),
  project: yup.string().required("Proyecto es requerido"),
  landSize: yup.string().required("Medidas del terreno son requeridas"),
  downPayment: yup.string().required("Enganche es requerido"),
  monthlyPayment: yup.string().required("Mensualidad es requerida"),
  appointmentDate: yup.date().nullable().optional(),
  type: yup.string().required("El tipo de contacto es requerido"),
});

export const LeadDrawer: React.FC<LeadDrawerProps> = ({
  open,
  onClose,
  lead,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: users } = useUsers();
  const { data: campaigns } = useProjects();
  const { data: types } = useTypes();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    watch,
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(leadSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      project: "",
      landSize: "",
      downPayment: "",
      monthlyPayment: "",
      appointmentDate: null,
      user: "",
      type: "",
    },
  });

  const handleClose = () => {
    reset({
      name: "",
      phone: "",
      email: "",
      project: "",
      landSize: "",
      downPayment: "",
      monthlyPayment: "",
      user: "",
      appointmentDate: null,
      type: "",
    });
    onClose();
  };

  const onSubmit = async (data: any) => {
    const userSelected = users?.find(
      (user: { name: string; lastName: string }) =>
        `${user.name} ${user.lastName}` === data.user
    );
    try {
      if (lead) {
        await updateOne(lead._id, {
          ...data,
          user: userSelected?._id,
        });
      } else {
        const createdLead = await createLead(data).then((r) => r.data);

        if (createdLead?.success === false) {
          enqueueSnackbar(createdLead?.message, { variant: "error" });
        }
      }
      handleClose();
    } catch (error) {
      enqueueSnackbar("Se ha generado un error", { variant: "error" });
    }
  };

  const selectedProject = watch("project");

  useEffect(() => {
    if (!lead) return;

    const currentValues = getValues();

    if (currentValues.name !== lead.fullName) {
      if (lead) {
        reset({
          ...lead,
          name: lead.fullName,
          phone: lead.phone,
          email: lead.email,
          project: lead.campaign,
          landSize: lead.landMeasurement,
          downPayment: lead.hitch,
          monthlyPayment: lead.mensuality,
          type: lead.type,
          user: `${get(lead, "user.name")} ${get(lead, "user.lastName")}`,
          appointmentDate: lead.appointmentDate
            ? dayjs(lead.appointmentDate).toDate()
            : null,
        });
      }
    }
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      PaperProps={{
        sx: {
          width: isMobile ? "80vw" : "30vw",
          maxWidth: isMobile ? "80vw" : "30vw",
        },
      }}
      onClose={handleClose}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ color: DEFAULT_COLOR }}
              >
                {lead ? "Editar Contacto" : "Agregar Contacto"}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Nombre"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Teléfono"
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Correo Electrónico"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="project"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={campaigns.map((c) => c.name) || []}
                    value={field.value}
                    onChange={(_, value) => {
                      reset({
                        ...getValues(),
                        landSize: "",
                        downPayment: "",
                        monthlyPayment: "",
                        ...(value && {
                          project: String(value),
                        }),
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Proyecto"
                        error={!!errors.project}
                        helperText={errors.project?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    disableClearable
                    options={types.map((c) => c.name) || []}
                    value={field.value}
                    onChange={(_, value) => setValue("type", value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de contacto"
                        error={!!errors.type}
                        helperText={errors.type?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            {lead ? (
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="user"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      disableClearable
                      value={field.value}
                      options={
                        users?.map((user) => `${user.name} ${user.lastName}`) ||
                        []
                      }
                      onChange={(_, value) => setValue("user", value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Asesor / Cerrador"
                          error={!!errors.user}
                          helperText={errors.user?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            ) : null}

            <Grid size={{ xs: 12 }}>
              <Controller
                name="landSize"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={landSizes[selectedProject as Projects] || []}
                    value={field.value || ""}
                    onChange={(_, value) => field.onChange(value)}
                    onInputChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Superficie propuesta"
                        error={!!errors.landSize}
                        helperText={errors.landSize?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="downPayment"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={downPayments[selectedProject as Projects] || []}
                    value={field.value || ""}
                    onChange={(_, value) => field.onChange(value)}
                    onInputChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Enganche propuesto"
                        error={!!errors.downPayment}
                        helperText={errors.downPayment?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="monthlyPayment"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={monthlyPayments[selectedProject as Projects] || []}
                    value={field.value || ""}
                    onChange={(_, value) => field.onChange(value)}
                    onInputChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Mensualidad propuesta"
                        error={!!errors.monthlyPayment}
                        helperText={errors.monthlyPayment?.message}
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Controller
                name="appointmentDate"
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    label="Fecha y Hora de Cita"
                    value={field.value}
                    onChange={field.onChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.appointmentDate,
                        helperText: errors.appointmentDate?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ backgroundColor: YELLOW_COLOR }}
              >
                {lead ? "Editar" : "Guardar"}
              </Button>
            </Grid>
          </Grid>
        </form>
      </LocalizationProvider>
    </Drawer>
  );
};
